import { A, useNavigate } from "@solidjs/router";
import { ArrowRight, CheckCircle2, Loader2, Lock, ShieldCheck } from "lucide-solid";
import { For, Show, createSignal } from "solid-js";

import { PlatformMark } from "@/components/brand/PlatformMark";
import { Button } from "@/components/ui/Button";
import { FieldError, Input, Label } from "@/components/ui/Input";
import { api } from "@/lib/api";
import { HOSTING_NOTICE } from "@/features/privacy/hosting-notice";

import { SWISS_CANTONS, slugifySubdomain } from "./subdomain";

export function SignupPage() {
  const navigate = useNavigate();
  const [schoolName, setSchoolName] = createSignal("");
  const [legalName, setLegalName] = createSignal("");
  const [subdomain, setSubdomain] = createSignal("");
  const [subTouched, setSubTouched] = createSignal(false);
  const [address, setAddress] = createSignal("");
  const [postalCode, setPostalCode] = createSignal("");
  const [city, setCity] = createSignal("");
  const [canton, setCanton] = createSignal("VD");
  const [firstName, setFirstName] = createSignal("");
  const [lastName, setLastName] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [done, setDone] = createSignal(false);

  const onSchoolName = (value: string) => {
    setSchoolName(value);
    if (!subTouched()) setSubdomain(slugifySubdomain(value));
  };

  const onSubmit = async (event: Event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/public/onboarding/applications", {
        method: "POST",
        body: JSON.stringify({
          school_name: schoolName(),
          legal_name: legalName() || schoolName(),
          subdomain: subdomain(),
          address_line1: address(),
          postal_code: postalCode(),
          city: city(),
          canton: canton(),
          first_name: firstName(),
          last_name: lastName(),
          email: email(),
          password: password(),
        }),
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d’enregistrer la demande.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main class="min-h-screen w-full bg-[var(--bg-surface)] text-[var(--fg-default)] lg:grid lg:grid-cols-[0.92fr_1.08fr]">
      <aside class="ink-panel relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14">
        <div class="ink-grid pointer-events-none absolute inset-0" />
        <header class="relative z-10 flex items-center gap-3">
          <PlatformMark class="h-10 w-10" />
          <div>
            <p class="font-display text-[19px] font-semibold text-[var(--ink-fg)]">Scola</p>
            <p class="mt-1 text-[12px] text-[var(--ink-fg-muted)]">Pour chaque centre, son espace.</p>
          </div>
        </header>
        <section class="relative z-10 max-w-lg">
          <p class="eyebrow !text-[#8ea6ff]">Nouvel établissement</p>
          <h1 class="mt-4 font-display text-[2.6rem] font-semibold leading-[1.05] tracking-[-0.025em] text-[var(--ink-fg)]">
            Ouvrez le compte de votre école.
          </h1>
          <p class="mt-5 text-[15.5px] leading-relaxed text-[var(--ink-fg-muted)]">
            La demande est enregistrée tout de suite. L’accès ERP n’est activé qu’après paiement —
            vous recevez alors votre espace, isolé des autres centres.
          </p>
          <ul class="mt-7 grid gap-2.5 text-[13.5px] text-[var(--ink-fg-muted)]">
            <li class="flex gap-2.5">
              <span class="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#8ea6ff]" />
              Un compte direction par école, protégé par 2FA
            </li>
            <li class="flex gap-2.5">
              <span class="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#8ea6ff]" />
              Catalogue, sessions, émargement QR, attestations
            </li>
            <li class="flex gap-2.5">
              <span class="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#8ea6ff]" />
              {HOSTING_NOTICE}
            </li>
          </ul>
        </section>
        <footer class="relative z-10 text-[12px] text-[var(--ink-fg-subtle)]">
          Bimcheck Consulting · {new Date().getFullYear()}
        </footer>
      </aside>

      <section class="flex min-h-screen flex-col justify-center px-6 py-12 lg:px-14 xl:px-20">
        <div class="mx-auto w-full max-w-[480px] space-y-8">
          <header class="flex items-center gap-3 lg:hidden">
            <PlatformMark class="h-10 w-10" />
            <p class="font-display text-[17px] font-semibold text-[var(--fg-strong)]">Scola</p>
          </header>
          <Show
            when={!done()}
            fallback={
              <div class="space-y-5 rounded-[20px] border border-[var(--border-default)] bg-[var(--bg-elevated)] p-8">
                <CheckCircle2 class="text-[var(--success)]" size={32} />
                <h2 class="font-display text-[1.75rem] font-semibold text-[var(--fg-strong)]">
                  Demande enregistrée.
                </h2>
                <p class="text-[15px] leading-relaxed text-[var(--fg-muted)]">
                  Vous pourrez vous connecter dès que votre accès aura été activé après paiement.
                  Conservez l’adresse {email()} — c’est celle du compte de direction.
                </p>
                <Button onClick={() => navigate("/login")} size="lg">
                  Retour à la connexion
                </Button>
              </div>
            }
          >
            <div class="space-y-2">
              <p class="eyebrow">Créer un compte</p>
              <h2 class="font-display text-[2rem] font-semibold tracking-[-0.02em] text-[var(--fg-strong)]">
                Votre école sur Scola.
              </h2>
              <p class="text-[15px] text-[var(--fg-muted)]">
                Déjà un accès ?{" "}
                <A href="/login" class="font-semibold text-[var(--accent-alt)] hover:underline">
                  Se connecter
                </A>
              </p>
            </div>

            <form onSubmit={onSubmit} class="space-y-4">
              <div>
                <Label for="school_name">Nom de l’école</Label>
                <Input
                  id="school_name"
                  required
                  value={schoolName()}
                  onInput={(e) => onSchoolName(e.currentTarget.value)}
                  placeholder="Nom affiché de votre centre"
                  class="h-11"
                />
              </div>
              <div>
                <Label for="legal_name">Raison sociale (optionnel)</Label>
                <Input
                  id="legal_name"
                  value={legalName()}
                  onInput={(e) => setLegalName(e.currentTarget.value)}
                  placeholder="Identique au nom, si vous n’avez pas de Sàrl"
                  class="h-11"
                />
              </div>
              <div>
                <Label for="subdomain">Sous-domaine</Label>
                <Input
                  id="subdomain"
                  required
                  value={subdomain()}
                  onInput={(e) => {
                    setSubTouched(true);
                    setSubdomain(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                  }}
                  placeholder="mon-ecole"
                  class="h-11 font-mono"
                />
                <p class="mt-1.5 text-[12px] text-[var(--fg-subtle)]">
                  Adresse publique : {subdomain() || "mon-ecole"}.scola.app
                </p>
              </div>
              <div>
                <Label for="address">Adresse</Label>
                <Input
                  id="address"
                  required
                  value={address()}
                  onInput={(e) => setAddress(e.currentTarget.value)}
                  placeholder="Rue et numéro, propres à cette école"
                  class="h-11"
                />
              </div>
              <div class="grid gap-4 sm:grid-cols-[7rem_1fr_7rem]">
                <div>
                  <Label for="postal_code">NPA</Label>
                  <Input
                    id="postal_code"
                    required
                    inputMode="numeric"
                    maxlength={4}
                    value={postalCode()}
                    onInput={(e) => setPostalCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="1950"
                    class="h-11"
                  />
                </div>
                <div>
                  <Label for="city">Ville</Label>
                  <Input
                    id="city"
                    required
                    value={city()}
                    onInput={(e) => setCity(e.currentTarget.value)}
                    placeholder="Sion"
                    class="h-11"
                  />
                </div>
                <div>
                  <Label for="canton">Canton</Label>
                  <select
                    id="canton"
                    class="h-11 w-full rounded-[10px] border border-[var(--border-strong)] bg-[var(--bg-surface)] px-3 text-sm"
                    value={canton()}
                    onChange={(e) => setCanton(e.currentTarget.value)}
                  >
                    <For each={SWISS_CANTONS}>{(code) => <option value={code}>{code}</option>}</For>
                  </select>
                </div>
              </div>
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label for="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    required
                    value={firstName()}
                    onInput={(e) => setFirstName(e.currentTarget.value)}
                    class="h-11"
                  />
                </div>
                <div>
                  <Label for="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    required
                    value={lastName()}
                    onInput={(e) => setLastName(e.currentTarget.value)}
                    class="h-11"
                  />
                </div>
              </div>
              <div>
                <Label for="signup-email">E-mail de direction</Label>
                <Input
                  id="signup-email"
                  type="email"
                  required
                  value={email()}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                  placeholder="direction@ecole.ch"
                  class="h-11"
                />
              </div>
              <div>
                <Label for="signup-password">Mot de passe (12 caractères min.)</Label>
                <Input
                  id="signup-password"
                  type="password"
                  required
                  minLength={12}
                  value={password()}
                  onInput={(e) => setPassword(e.currentTarget.value)}
                  class="h-11"
                />
              </div>
              <Show when={error()}>
                <FieldError>{error()}</FieldError>
              </Show>
              <Button type="submit" disabled={submitting()} size="lg" class="w-full">
                <Show when={submitting()} fallback={<>Envoyer la demande <ArrowRight size={16} /></>}>
                  <Loader2 size={16} class="animate-spin" />
                  Envoi…
                </Show>
              </Button>
              <p class="flex items-center gap-2 text-[12px] text-[var(--fg-subtle)]">
                <Lock size={12} />
                <ShieldCheck size={12} />
                Pas d’accès avant activation. 2FA obligatoire pour le compte direction.
              </p>
            </form>
          </Show>
        </div>
      </section>
    </main>
  );
}
